import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { RiUpload2Line } from "react-icons/ri";
import { CustomInput } from "src/components/Inputs/CustomInput";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { parseCsvFile } from "src/services/dataTransfer";
import {
  StudentOption,
  StudentSearchMultiSelect,
} from "./StudentSearchMultiSelect";

interface RequestPaymentDrawerProps {
  paymentTemplateId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PaymentTemplateItem {
  id: string;
  name: string;
  description: string | null;
  amountInCents: number;
  currency: string;
}

export const RequestPaymentDrawer = ({
  paymentTemplateId,
  isOpen,
  onClose,
  onSuccess,
}: RequestPaymentDrawerProps) => {
  const toast = useAvelaToast();
  const organization = useOrganization();
  const organizationId = organization.toNullable()?.id;

  const [templateDetails, setTemplateDetails] =
    useState<PaymentTemplateItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedStudents, setSelectedStudents] = useState<StudentOption[]>([]);
  const [uploadedStudentIds, setUploadedStudentIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && organizationId && paymentTemplateId) {
      FSR.getPaymentTemplate({ path: { templateId: paymentTemplateId } })
        .then((response) => {
          if (!response.data) {
            throw new Error("No data returned from getPaymentTemplate");
          }
          setTemplateDetails({
            id: response.data.data.id,
            name: response.data.data.name,
            description: response.data.data.description,
            amountInCents: response.data.data.amountInCents,
            currency: response.data.data.currency,
          });
        })
        .catch((error) => {
          console.error("Failed to load payment template:", error);
          toast({
            title: "Failed to load payment template",
            description: "Please try again.",
            status: "error",
          });
        });
    }
  }, [isOpen, organizationId, paymentTemplateId, toast]);

  const handleClose = useCallback(() => {
    setSelectedStudents([]);
    setTemplateDetails(null);
    setUploadedStudentIds([]);
    onClose();
  }, [onClose]);

  const handleCsvUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget?.files?.[0];
      if (!file) return;

      parseCsvFile(
        file,
        (results: any) => {
          const ids = results.data
            .map((row: any) => row.id)
            .filter((id: string) => id);
          setUploadedStudentIds(ids);
          toast({
            title: "File uploaded",
            description: `${ids.length} student IDs loaded from CSV`,
            status: "success",
          });
        },
        (error: any) => {
          console.error("CSV parse error:", error);
          toast({
            title: "Failed to parse CSV",
            description: "Please check the file format and try again.",
            status: "error",
          });
        }
      );

      event.currentTarget.value = "";
    },
    [toast]
  );

  const handleSubmit = async () => {
    if (!paymentTemplateId || !templateDetails) return;

    const studentIds =
      uploadedStudentIds.length > 0
        ? uploadedStudentIds
        : selectedStudents.map((s) => s.id);

    if (studentIds.length === 0) return;

    setIsSubmitting(true);
    try {
      await FSR.createPayments({
        body: {
          payments: studentIds.map((studentId) => ({
            paymentTemplateId: paymentTemplateId,
            formId: null,
            personId: studentId,
            amountInCents: templateDetails.amountInCents,
            currency: templateDetails.currency,
            paymentStatus: "pending",
            stripePaymentIntentId: null,
            paidBy: null,
          })),
        },
      });

      toast({
        title: "Payment requests created",
        description: "Families will be prompted for payment.",
        status: "success",
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Failed to create payment requests:", error);
      toast({
        title: "Failed to create payment requests",
        description: "Please try again.",
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid =
    paymentTemplateId !== null &&
    templateDetails !== null &&
    (selectedStudents.length > 0 || uploadedStudentIds.length > 0);

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={handleClose}
      header="Request payment"
      footer={
        <HStack spacing={2} w="full">
          <Button
            variant="outline"
            flex={1}
            onClick={handleClose}
            colorScheme="gray"
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            flex={1}
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={isSubmitting}
          >
            Request payment
          </Button>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        <Text fontSize="sm" color="gray.600">
          Send a payment request for{" "}
          <Text as="span" fontWeight="semibold">
            {templateDetails?.name ?? "a payment"}
          </Text>
          . Families will be prompted to pay once they log in.{" "}
          <Link
            href="https://avelahelp.zendesk.com/hc/en-us/articles/47341300235021"
            color="primary.500"
            textDecoration="underline"
            isExternal
          >
            Learn more
          </Link>
          .
        </Text>

        <FormControl isRequired>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            mb={1.5}
          >
            Students to send payment request
          </FormLabel>
          <StudentSearchMultiSelect
            organizationId={organizationId ?? ""}
            selectedStudents={selectedStudents}
            onChange={setSelectedStudents}
            isDisabled={uploadedStudentIds.length > 0}
          />
          <FormHelperText fontSize="xs" color="gray.500" mt={1.5}>
            Payment request will be received by all parent/guardians related to
            the selected students.
          </FormHelperText>
        </FormControl>

        <VStack align="stretch" spacing={3}>
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            Or upload Student list
          </Text>
          <CustomInput
            type="file"
            accept=".csv"
            onChange={handleCsvUpload}
            isDisabled={selectedStudents.length > 0}
          >
            <Button
              variant="outline"
              colorScheme="gray"
              leftIcon={<RiUpload2Line />}
              isDisabled={selectedStudents.length > 0}
            >
              {uploadedStudentIds.length > 0
                ? `${uploadedStudentIds.length} students loaded`
                : "Upload file"}
            </Button>
          </CustomInput>
          <Text fontSize="xs" color="gray.500">
            You can upload 1 CSV file
          </Text>
        </VStack>
      </VStack>
    </ResizableDrawer>
  );
};
